import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * The places a page header offers for a dashboard's page to name itself,
 * beside the dashboard's own identity: the page's mark (flag, map, icon), its
 * name, and the line under it (position, next page, controls).
 *
 * A page switcher claims them (`usePresentationHeader`); while it does, the
 * header shows the dashboard and its page side by side, at the same height, so
 * the screen keeps one header and the rest of its height goes to the widgets.
 *
 * Two headers offer them: the presented (full-screen) one always, and the
 * standard page header when the dashboard asks for it
 * (`pagePresentation.titlePlacement: "header"`, see `pageTitlePlacement`).
 */
export interface PresentationHeaderSlots {
  mark: HTMLElement;
  title: HTMLElement;
  meta: HTMLElement;
}

/** Which header offers the slots: the full-screen presentation, or the standard page header. */
export type PageHeaderHostMode = "presentation" | "page";

export interface PresentationHeaderContextValue {
  /** The slots once the header shows them, `null` until then. */
  slots: PresentationHeaderSlots | null;
  /** Asks the header for its page slots; the returned function gives them back. */
  claim: () => () => void;
  /** Absent is `presentation`, the original host. */
  mode?: PageHeaderHostMode;
  /**
   * Hands the header the page commands it lists in its More menu (the standard
   * page header, whose page line keeps only the position and the next page).
   */
  setCommands?: (commands: PageCommands | null) => void;
  /**
   * The header's place for the page controls (previous, pause with its dwell
   * ring, next), on its right before the filters and set apart from them. Offered
   * whether or not a page names itself in the header: a dashboard showing its
   * controls puts them here whenever its navigation has no bar of its own
   * (page title in the header, full screen, hidden navigation).
   */
  controls?: HTMLElement | null;
}

/**
 * Previous / next page and pause / resume, as the header's More menu lists them,
 * in a group of their own. The keyboard does the same (← →, Space).
 */
export interface PageCommands {
  /** Position of the current page, from 1, and how many pages there are. */
  position: number;
  count: number;
  /** Pause / resume only while the pages rotate. */
  automatic: boolean;
  paused: boolean;
  move: (offset: number) => void;
  togglePause: () => void;
}

export const PresentationHeaderContext = createContext<PresentationHeaderContextValue | null>(null);

/** The header that would take the page's identity, or `null` when none offers it. */
export function usePresentationHeaderMode(): PageHeaderHostMode | null {
  const context = useContext(PresentationHeaderContext);
  return context ? (context.mode ?? "presentation") : null;
}

/**
 * The header's page slots when a header offers them and `wanted`, otherwise
 * `null` (the caller then shows its own page header in the content).
 */
export function usePresentationHeader(wanted: boolean): PresentationHeaderSlots | null {
  const context = useContext(PresentationHeaderContext);
  const claim = context?.claim;
  useLayoutEffect(() => (wanted && claim ? claim() : undefined), [wanted, claim]);
  return wanted ? (context?.slots ?? null) : null;
}

/** The header's page-controls place when one is offered and `wanted`, otherwise `null`. */
export function usePageControlsSlot(wanted: boolean): HTMLElement | null {
  const context = useContext(PresentationHeaderContext);
  return wanted ? (context?.controls ?? null) : null;
}

/**
 * Gives the header's More menu the page commands while `commands` is set; the
 * callbacks may change at every render, only the state shown is compared.
 */
export function usePageHeaderCommands(commands: PageCommands | null) {
  const setCommands = useContext(PresentationHeaderContext)?.setCommands;
  // The menu calls the latest callbacks through stable functions.
  const latest = useRef(commands);
  useLayoutEffect(() => {
    latest.current = commands;
  });
  const move = useCallback((offset: number) => latest.current?.move(offset), []);
  const togglePause = useCallback(() => latest.current?.togglePause(), []);
  const active = commands !== null;
  const { position = 0, count = 0, automatic = false, paused = false } = commands ?? {};
  useEffect(() => {
    if (!setCommands || !active) return;
    setCommands({ position, count, automatic, paused, move, togglePause });
  }, [setCommands, active, position, count, automatic, paused, move, togglePause]);
  useEffect(
    () => (setCommands && active ? () => setCommands(null) : undefined),
    [setCommands, active],
  );
}

/** The slot elements a header renders while it is claimed. */
export interface PageHeaderSlotRefs {
  mark: (element: HTMLElement | null) => void;
  title: (element: HTMLElement | null) => void;
  meta: (element: HTMLElement | null) => void;
  /** The page controls' place, rendered by the header at all times (empty, it takes no room). */
  controls: (element: HTMLElement | null) => void;
}

/**
 * The header side of the slots: whether a page has claimed them (`paged`: the
 * header then renders the page's places, with `refs`), and the context value
 * to provide around the content.
 */
export function usePageHeaderHost(mode: PageHeaderHostMode) {
  const [claims, setClaims] = useState(0);
  const claim = useCallback(() => {
    setClaims((count) => count + 1);
    return () => setClaims((count) => count - 1);
  }, []);
  const [mark, setMark] = useState<HTMLElement | null>(null);
  const [title, setTitle] = useState<HTMLElement | null>(null);
  const [meta, setMeta] = useState<HTMLElement | null>(null);
  const [controls, setControls] = useState<HTMLElement | null>(null);
  const [commands, setCommands] = useState<PageCommands | null>(null);
  const paged = claims > 0;
  const slots: PresentationHeaderSlots | null =
    paged && mark && title && meta ? { mark, title, meta } : null;
  const context = useMemo<PresentationHeaderContextValue>(
    () => ({ slots, claim, mode, setCommands, controls }),
    [slots, claim, mode, controls],
  );
  const refs = useMemo<PageHeaderSlotRefs>(
    () => ({ mark: setMark, title: setTitle, meta: setMeta, controls: setControls }),
    [],
  );
  return { paged, context, refs, commands: paged ? commands : null };
}
